'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { SportsEventsClient } from '@bip/api-client';
import { toSlug } from '@bip/shared';
import { CONCERTS_API_BASE_URL } from '../config';
import DatePicker from 'react-datepicker';
// react-datepicker CSS imported in globals.css

// `sportName` intentionally NOT surfaced on the concerts SearchBar —
// event-type is not a concerts-relevant facet, so suggest rows show
// only the item name + count (dropped from the type so accidental
// re-adds are caught at compile time).
type Item = {
  id: number;
  name: string;
  eventCount?: number;
  /** Artist / participant name on an event-suggestion row. Backend
   *  populates it on every concerts event suggestion (see
   *  `events.repository.ts` `extractArtistName`). Falsy → no
   *  sub-line rendered. Only surfaced on the `events` group. */
  artistName?: string;
};

/** Format date as "Thu, 26 Mar" */
function fmtShort(d: Date): string {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

// Detect groupType + id from URL path for auto-selecting search item
function detectSelectedFromUrl(
  name?: string,
): { groupType: string; id: number; name: string } | null {
  if (!name || typeof window === 'undefined') return null;
  const path = window.location.pathname;
  const routeMap: Record<string, string> = {
    '/concerts/city/': 'cities',
    '/concerts/artist/': 'participants',
    '/concerts/venue/': 'venues',
  };
  for (const [prefix, groupType] of Object.entries(routeMap)) {
    if (path.startsWith(prefix)) {
      const slug = path.slice(prefix.length);
      // Extract id from slug: "london-123" → 123
      const match = slug.match(/-(\d+)$/);
      const id = match ? parseInt(match[1], 10) : 0;
      return { groupType, id, name };
    }
  }
  return null;
}

export function SearchBar({ initialSearch }: { initialSearch?: string } = {}) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [search, setSearch] = useState(initialSearch || searchParams.get('search') || '');
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userTyped, setUserTyped] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedItem, setSelectedItem] = useState<{
    groupType: string;
    id: number;
    name: string;
  } | null>(() => detectSelectedFromUrl(initialSearch));

  const [results, setResults] = useState<{
    events: Item[];
    cities: Item[];
    venues: Item[];
    /** Artist / participant suggestions (backend `artists`). Selecting
     *  one navigates via the `participants` groupType route → the
     *  `/concerts/artist/<slug>` listing. Empty by default; only
     *  populated once a 3+ char query fires the fetch. */
    artists: Item[];
  }>({ events: [], cities: [], venues: [], artists: [] });

  // Date dropdown state — initialize from URL params
  const paramDateFrom = searchParams.get('dateFrom') || '';
  const paramDateTo = searchParams.get('dateTo') || '';
  const paramDateRange = searchParams.get('dateRange') || '';
  const paramDatePreset = searchParams.get('datePreset') || '';

  function parseDateStr(str: string): Date | null {
    if (!str) return null;
    // Try DD/MM/YYYY
    const parts = str.split('/');
    if (parts.length === 3) return new Date(+parts[2], +parts[1] - 1, +parts[0]);
    // Try ISO
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }

  function getInitialDateLabel(): string {
    if (paramDatePreset) return paramDatePreset;
    if (paramDateRange === 'today') return 'Today';
    if (paramDateRange === 'next7days' || paramDateRange === 'thisWeek') return 'Next 7 Days';
    if (paramDateRange === 'thisMonth') return 'This Month';
    if (paramDateFrom && paramDateTo) {
      const from = parseDateStr(paramDateFrom);
      const to = parseDateStr(paramDateTo);
      if (from && to) return `${fmtShort(from)} - ${fmtShort(to)}`;
      return `${paramDateFrom} – ${paramDateTo}`;
    }
    return 'All Dates';
  }

  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [dateLabel, setDateLabel] = useState(getInitialDateLabel);
  const [datePreset, setDatePreset] = useState(paramDatePreset);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customFrom, setCustomFrom] = useState<Date | null>(() => parseDateStr(paramDateFrom));
  const [customTo, setCustomTo] = useState<Date | null>(() => parseDateStr(paramDateTo));
  const [dateFrom, setDateFrom] = useState(paramDateFrom);
  const [dateTo, setDateTo] = useState(paramDateTo);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const dateWrapperRef = useRef<HTMLDivElement>(null);
  const [dateDropdownUp, setDateDropdownUp] = useState(false);

  // Date helpers — all in DD/MM/YYYY format
  const fmtDate = (d: Date) => d.toLocaleDateString('en-GB');
  const now = new Date();
  const todayStr = fmtDate(now);
  const in7DaysDate = new Date(now);
  in7DaysDate.setDate(now.getDate() + 7);
  const in7DaysStr = fmtDate(in7DaysDate);
  //const monthStartStr = fmtDate(new Date(now.getFullYear(), now.getMonth(), 1));
  const monthEndStr = fmtDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));

  // Auto-populate from localStorage on landing page only (/sports or /).
  // Also listens for `concertsHomeCityResolved` — dispatched by
  // `ConcertsHomeListing` when its 3-tier resolver (IP-geo →
  // localStorage → London) picks a city. That path writes to
  // localStorage too, but the SearchBar mount may run before the
  // write completes, so the event covers the race: on receipt we
  // re-run the same lookup with the resolved name and set the
  // "selected city" state, which surfaces as a pre-filled chip in
  // the destination field.
  const pathname = usePathname();
  useEffect(() => {
    if (pathname !== '/concerts' && pathname !== '/') return;
    if (initialSearch || search) return;

    const client = new SportsEventsClient(CONCERTS_API_BASE_URL);

    const populateFromName = (cityName: string) => {
      if (!cityName) return;
      client
        .getSearchResultData(cityName)
        .then((res) => {
          const lower = cityName.toLowerCase();
          const match = res?.cities?.find(
            (c: any) =>
              c.name?.toLowerCase() === lower ||
              c.name?.toLowerCase().startsWith(lower) ||
              lower.startsWith(c.name?.toLowerCase().split(',')[0]),
          );
          if (match?.id) {
            setSearch(cityName);
            setSelectedItem({
              groupType: 'cities',
              id: match.id,
              name: match.name || cityName,
            });
          }
        })
        .catch(() => {});
    };

    const onResolved = (e: Event) => {
      const detail = (e as CustomEvent<{ id: number; name: string }>).detail;
      if (!detail?.name) return;
      // Skip the lookup — the resolver already matched a catalogue
      // row, so use its id directly.
      setSearch(detail.name);
      setSelectedItem({ groupType: 'cities', id: detail.id, name: detail.name });
    };
    window.addEventListener('concertsHomeCityResolved', onResolved);

    try {
      const stored = localStorage.getItem('theatre-searches-city');
      if (!stored) {
        return () => window.removeEventListener('concertsHomeCityResolved', onResolved);
      }
      const data = JSON.parse(stored);
      const cityName = data?.city;
      if (!cityName) {
        return () => window.removeEventListener('concertsHomeCityResolved', onResolved);
      }
      populateFromName(cityName);
    } catch {
      /* ignore */
    }
    return () => window.removeEventListener('concertsHomeCityResolved', onResolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Listen for filter changes coming from the listing-page filter bar so the
  // SearchBar's date label / state mirrors whatever EventsFilters wrote into
  // the URL. EventsFilters uses `history.replaceState` + dispatches a
  // `filtersChanged` event (not a full Next navigation), so `searchParams`
  // here doesn't auto-update — we re-read the URL on the event.
  useEffect(() => {
    const syncFromUrl = () => {
      const sp = new URLSearchParams(window.location.search);
      const newDateFrom = sp.get('dateFrom') || '';
      const newDateTo = sp.get('dateTo') || '';
      const newPreset = sp.get('datePreset') || '';
      const newRange = sp.get('dateRange') || '';

      setDateFrom(newDateFrom);
      setDateTo(newDateTo);
      setDatePreset(newPreset);
      setCustomFrom(parseDateStr(newDateFrom));
      setCustomTo(parseDateStr(newDateTo));

      // Recompute the displayed label using the same precedence as on
      // initial mount (preset > range alias > explicit from–to > "All Dates").
      let label: string;
      if (newPreset) {
        label = newPreset;
      } else if (newRange === 'today') {
        label = 'Today';
      } else if (newRange === 'next7days' || newRange === 'thisWeek') {
        label = 'Next 7 Days';
      } else if (newRange === 'thisMonth') {
        label = 'This Month';
      } else if (newDateFrom && newDateTo) {
        const from = parseDateStr(newDateFrom);
        const to = parseDateStr(newDateTo);
        label =
          from && to ? `${fmtShort(from)} - ${fmtShort(to)}` : `${newDateFrom} – ${newDateTo}`;
      } else {
        label = 'All Dates';
      }
      setDateLabel(label);
    };
    window.addEventListener('filtersChanged', syncFromUrl);
    return () => window.removeEventListener('filtersChanged', syncFromUrl);
  }, []);

  // Close search results dropdown on outside click
  useEffect(() => {
    const handler = (e: any) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Close date dropdown on outside click (but not when clicking inside datepicker)
  const showDateDropdownRef = useRef(false);
  showDateDropdownRef.current = showDateDropdown;
  useEffect(() => {
    const handler = (e: any) => {
      const target = e.target as HTMLElement;
      // Never close if clicking inside the date wrapper or any datepicker element
      if (dateWrapperRef.current?.contains(target)) return;
      if (
        target?.closest?.(
          '.react-datepicker, .react-datepicker__month-container, .react-datepicker-popper',
        )
      )
        return;
      // Only reset state when the dropdown is actually open to avoid unnecessary re-renders
      if (showDateDropdownRef.current) {
        setShowDateDropdown(false);
        setShowCustomPicker(false);
        setCustomFrom(parseDateStr(dateFrom));
        setCustomTo(parseDateStr(dateTo));
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dateFrom, dateTo]);

  // Search logic — only trigger when user types, not on initial mount
  useEffect(() => {
    if (!userTyped || search.length < 3) {
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    setShowDropdown(true);

    const timer = setTimeout(async () => {
      const q = search;
      const sportsEventsApiUrl = CONCERTS_API_BASE_URL;
      const eventsClient = new SportsEventsClient(sportsEventsApiUrl);
      try {
        const raw = await eventsClient.getSearchResultData(q);
        setResults({
          events: raw?.events ?? [],
          cities: raw?.cities ?? [],
          // Filter out venues with zero upcoming concerts — a venue
          // suggestion that leads to an empty listing is worse UX
          // than dropping the row entirely. The backend attaches
          // `eventCount` per venue (from the count aggregate in
          // `searchEventData`); missing / 0 means no concerts in the
          // catalogue for that venue. Only venues get this gate per
          // spec — events / cities / participants / tournaments /
          // eventTypes still surface even at zero.
          venues: (raw?.venues ?? []).filter(
            (v: { eventCount?: number }) => (v?.eventCount ?? 0) > 0,
          ),
          // Artists follow the same zero-count gate as venues — an
          // artist with no upcoming concerts on the platform would
          // route to an empty listing. The backend's aggregation
          // already restricts to future events (`futureMatch`) so
          // any row with `eventCount > 0` is guaranteed to have at
          // least one upcoming concert.
          artists: (raw?.artists ?? []).filter(
            (a: { eventCount?: number }) => (a?.eventCount ?? 0) > 0,
          ),
        });
      } catch {
        // API unavailable — leave results as empty default
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const selectOption = (groupType: string, id: number, name: string) => {
    setSearch(name);
    setSelectedItem({ groupType, id, name });
    setShowDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Empty-search guard — purely "no selection AND empty input".
    // A pre-populated `search` on landing (initialised from
    // `initialSearch || searchParams.get('search')` at mount time)
    // is already present in `search` state, so an unchanged form
    // submits the SAME keyword the URL carried — no separate URL
    // fallback needed. The previous URL-based bypass
    // (`urlSearch.length >= 1`) let a "type → clear (X) → submit"
    // sequence slip through because the earlier `?search=…` was
    // still in the URL; the user had EXPLICITLY cleared the input,
    // and we must honour that instead of silently re-submitting the
    // stale URL keyword. Same rule applies to the `!userTyped`
    // bypass we already dropped for the same reason.
    const hasUsableKeyword = search.trim().length >= 1;
    if (!selectedItem && !hasUsableKeyword) {
      setSearchError('Search keyword is required.');
      return;
    }
    setSearchError('');

    // Build date query string to append
    const dateParams = new URLSearchParams();
    if (dateFrom) dateParams.set('dateFrom', dateFrom);
    if (dateTo) dateParams.set('dateTo', dateTo);
    if (datePreset) dateParams.set('datePreset', datePreset);
    const dateQs = dateParams.toString();
    //const appendDate = dateQs ? (dateQs.startsWith('?') ? dateQs : `?${dateQs}`) : '';
    const joinChar = (url: string) => (url.includes('?') ? '&' : '?');
    let baseUrl = `/concerts/events?search=${encodeURIComponent(search)}`;
    if (selectedItem) {
      const { groupType, id, name } = selectedItem;

      // City-submit localStorage seeding (`theatre-searches-city` +
      // hotels `searches[]`) used to live here, but the fetch was
      // racing the `router.push` below — the navigation cancelled
      // the request mid-flight and the writes never landed. The
      // logic now lives in `CityRecentSearchSync`, mounted on the
      // `/concerts/city/[slug]` listing page, where it runs after
      // navigation completes on a stable React tree.

      const baseRoutes: Record<string, string> = {
        // "Artist" is the canonical concerts label; picking an
        // artist suggestion routes to `/concerts/artist/`.
        participants: `/concerts/artist/${toSlug(name, id)}`,
        cities: `/concerts/city/${toSlug(name, id)}`,
        venues: `/concerts/venue/${toSlug(name, id)}`,
        // Event suggestions route with `?search=<name>` ONLY (spec
        // 2026-08-10 revised): picking an event from the dropdown
        // should surface EVERY concert with that name (recurring
        // tour dates, multi-night residencies) — not just the one
        // row the user happened to click. The backend `search`
        // filter runs the same name-match keyword search a manual
        // typed search does, so both routes yield the same result
        // set. `eventId` is deliberately NOT sent here so the
        // listing isn't narrowed to a single row.
        //
        // Preserving `search=<name>` in the URL is also load-bearing
        // for the filter flow on the listing page: `EventsFilters`
        // clones `URLSearchParams` before writing any filter change,
        // so `search` (and therefore the concert name in the URL)
        // survives every filter toggle / chip removal until the
        // user explicitly hits "Clear All".
        events: `/concerts/events?search=${encodeURIComponent(name)}`,
      };
      baseUrl = baseRoutes[groupType] ?? `/concerts/events?search=${encodeURIComponent(name)}`;
      router.push(dateQs ? `${baseUrl}${joinChar(baseUrl)}${dateQs}` : baseUrl);
      return;
    }

    router.push(dateQs ? `${baseUrl}${joinChar(baseUrl)}${dateQs}` : baseUrl);
    // Should never reach here — selectedItem is handled above
  };

  const noResults =
    !loading &&
    !results?.events?.length &&
    !results?.cities?.length &&
    !results?.venues?.length &&
    !results?.artists?.length;

  const dateOptions = [
    { label: 'All Dates', from: '', to: '' },
    { label: 'Today', from: todayStr, to: todayStr },
    { label: 'Next 7 Days', from: todayStr, to: in7DaysStr },
    { label: `This Month`, from: todayStr, to: monthEndStr },
  ];

  return (
    <form onSubmit={handleSubmit} className="search-form-wrap">
      <div className="search-bar">
        {/* Date dropdown */}
        <div className="location" ref={dateWrapperRef}>
          <div
            onClick={() => {
              setShowDateDropdown((v) => {
                if (!v && dateWrapperRef.current) {
                  const rect = dateWrapperRef.current.getBoundingClientRect();
                  const spaceBelow = window.innerHeight - rect.bottom;
                  setDateDropdownUp(spaceBelow < 350);
                }
                if (v) {
                  // Closing: reset custom picker to applied state
                  setShowCustomPicker(false);
                  setCustomFrom(parseDateStr(dateFrom));
                  setCustomTo(parseDateStr(dateTo));
                }
                return !v;
              });
            }}
            style={{ cursor: 'pointer' }}
          >
            {dateLabel} <i className="fa-solid fa-angle-down pl-2"></i>
          </div>

          {showDateDropdown && (
            <div className={`dropdown-menu show${dateDropdownUp ? ' dropdown-menu-up' : ''}`}>
              {dateOptions.map(({ label, from, to }) => (
                <button
                  key={label}
                  type="button"
                  className="dropdown-item"
                  onClick={() => {
                    setDateLabel(label);
                    setDateFrom(from);
                    setDateTo(to);
                    setDatePreset(label === 'All Dates' ? '' : label);
                    setShowCustomPicker(false);
                    setCustomFrom(null);
                    setCustomTo(null);
                    setShowDateDropdown(false);
                  }}
                >
                  {label}
                </button>
              ))}

              <button
                type="button"
                className="dropdown-item"
                onClick={() => setShowCustomPicker((v) => !v)}
              >
                Custom dates <i className="fa-regular fa-calendar pl-2"></i>
              </button>

              {showCustomPicker && (
                <div className="px-3 py-2 customdate-picker">
                  <DatePicker
                    selected={customFrom}
                    onChange={(dates) => {
                      const [start, end] = dates as [Date | null, Date | null];
                      setCustomFrom(start);
                      setCustomTo(end);
                    }}
                    startDate={customFrom}
                    endDate={customTo}
                    selectsRange
                    inline
                    monthsShown={2}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                  />
                  {customFrom && (
                    <p className="small text-muted mt-2 mb-1">
                      {fmtShort(customFrom)}
                      {customTo ? ` - ${fmtShort(customTo)}` : ' - select end date'}
                    </p>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'flex-end',
                      paddingTop: '10px',
                      marginTop: '10px',
                      borderTop: '1px solid #eee',
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setShowCustomPicker(false);
                        setCustomFrom(null);
                        setCustomTo(null);
                        setDateFrom('');
                        setDateTo('');
                        setDatePreset('');
                        setDateLabel('All Dates');
                        setShowDateDropdown(false);
                      }}
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        const ddmmyyyy = (d: Date) => d.toLocaleDateString('en-GB');
                        const from = ddmmyyyy(customFrom!);
                        const to = ddmmyyyy(customTo!);
                        setDateFrom(from);
                        setDateTo(to);
                        setDateLabel(`${fmtShort(customFrom!)} - ${fmtShort(customTo!)}`);
                        setDatePreset('');
                        setShowDateDropdown(false);
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search input + results dropdown */}
        <div className="search-input" ref={wrapperRef}>
          <i className="fa-solid fa-magnifying-glass fontsize20 s-search-text"></i>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSelectedItem(null);
              setUserTyped(true);
              if (e.target.value.trim()) setSearchError('');
            }}
            onFocus={() => userTyped && search.length >= 3 && setShowDropdown(true)}
            placeholder="Search by Concert/Artist/Venue/City"
            className="search-input-field"
          />
          {search && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={() => {
                setSearch('');
                setSelectedItem(null);
                setShowDropdown(false);
                // Clear the cross-domain "last searched city" blob
                // (spec 2026-08-06). Without this, the next visit's
                // priority-1 auto-populate would immediately re-seed
                // the same city the user just deliberately removed.
                try {
                  localStorage.removeItem('theatre-searches-city');
                } catch { /* localStorage disabled — nothing to do */ }
              }}
              aria-label="Clear search"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}

          {searchError && (
            <p
              style={{
                color: '#d4111e',
                fontSize: '12px',
                margin: '4px 0 0 0',
                position: 'absolute',
                bottom: '-18px',
                left: 50,
              }}
            >
              {searchError}
            </p>
          )}

          {/* dropdown rendered outside search-input, below search-bar */}
        </div>

        <button type="submit" className="search-btn btn-lg">
          <span className="">Search</span>
          {/* <span className="">
            <i className="fa-solid fa-magnifying-glass fontsize20"></i>
          </span> */}
        </button>
      </div>

      {/* Search results dropdown — positioned relative to form */}
      {showDropdown && (
        <div className="search-results-dropdown">
          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '1.5rem 0',
              }}
            >
              <div className="spinner-border spinner-border-sm text-secondary" role="status" />
            </div>
          )}

          {!loading && noResults && (
            <span className="dropdown-item text-muted">No result found</span>
          )}

          {/* Display order: Event → City → Artists → Tournament → Venue → Concer.
              Events lead so an exact-name match like "England vs Spain"
              is the first thing the user sees — clicking it routes to
              the all-events listing filtered by name + the current
              date range. */}

          {results.events.length > 0 && (
            <>
              <h6 className="dropdown-header">Concerts</h6>
              {results.events.map((i) => (
                <button
                  key={`e-${i.id}`}
                  type="button"
                  className="dropdown-item"
                  onClick={() => selectOption('events', i.id, i.name)}
                >
                  <span className="d-flex flex-column">
                    <span>{i.name}</span>
                    {/* Artist sub-line — surfaced when the backend
                        emits `artistName` on the event suggestion.
                        Renders even when the event name already
                        contains the artist ("BTS - Bogota") because
                        Salman's spec calls for it explicitly ("even if
                        the concert name and artist name are the
                        same"). Suppressed when empty. */}
                    {/* {i.artistName ? (
                      <small className="text-muted">Artist: {i.artistName}</small>
                    ) : null} */}
                  </span>
                  {<span className="search-count">(1)</span>}
                </button>
              ))}
            </>
          )}

          {results.artists.length > 0 && (
            <>
              <h6 className="dropdown-header">Artists</h6>
              {results.artists.map((i) => (
                <button
                  key={`a-${i.id}`}
                  type="button"
                  className="dropdown-item"
                  onClick={() => selectOption('participants', i.id, i.name)}
                >
                  {i.name}
                  {i.eventCount ? (
                    <span className="search-count">({i.eventCount})</span>
                  ) : (
                    <span className="search-count">(0)</span>
                  )}
                </button>
              ))}
            </>
          )}

{results.venues.length > 0 && (
            <>
              <h6 className="dropdown-header">Venue</h6>
              {results.venues.map((i) => (
                <button
                  key={`v-${i.id}`}
                  type="button"
                  className="dropdown-item"
                  onClick={() => selectOption('venues', i.id, i.name)}
                >
                  {i.name}
                  {i.eventCount ? (
                    <span className="search-count">({i.eventCount})</span>
                  ) : (
                    <span className="search-count">(0)</span>
                  )}
                </button>
              ))}
            </>
          )}

          {results.cities.length > 0 && (
            <>
              <h6 className="dropdown-header">City</h6>
              {results.cities.map((i) => (
                <button
                  key={`c-${i.id}`}
                  type="button"
                  className="dropdown-item"
                  onClick={() => selectOption('cities', i.id, i.name)}
                >
                  {i.name}
                  {i.eventCount ? (
                    <span className="search-count">({i.eventCount})</span>
                  ) : (
                    <span className="search-count">(0)</span>
                  )}
                </button>
              ))}
            </>
          )}

        </div>
      )}
    </form>
  );
}
